import Vue from 'vue/dist/vue.esm';
import sweetAlert from 'sweetalert';
import I18n from '../../i18n-js/index.js.erb';
import SidePanel from './sidepanel';

// Track Overview Survey Question graphs. Re-fetches the server-rendered roll-up cards
// (which use the same audience-scoped SurveyQuestionRollup calculation as the full page)
// and swaps them in, so the visible counts match a just-saved response without a full
// reload. The cards are plain HTML (no Vue chart), so we only re-bind their data-sidepanel
// drill-down links after swapping. No-ops when the page has no Survey Question graphs.
function refreshSurveyQuestionGraphs() {
  const $section = $('.js-survey-question-graphs[data-refresh-url]');
  if (!$section.length) return;

  $.get($section.data('refresh-url')).done((html) => {
    $section.html(html);
    $section
      .find('[data-sidepanel]')
      .not((_index, el) => el.dataset.sidepanel === 'false')
      .on('click', SidePanel.loadAndShow.bind(SidePanel));
  });
}

// Track Overview Survey Completed card. Re-fetches the server-rendered card (which
// uses the same read-time completion calculation as the full page) and swaps it in,
// so the counts reflect a just-saved response without a full reload. The numeric
// counts are plain HTML and update regardless; the bar-chart is a Vue component, so
// we re-mount Vue on the swapped card (mirroring SidePanel's `new Vue({ el })`).
let surveyCompletedCardVm = null;

function refreshSurveyCompletedCard() {
  const $hook = $('.js-survey-completed-card[data-refresh-url]');
  if (!$hook.length) return; // not on the Overview page

  $.get($hook.data('refresh-url')).done((html) => {
    if (surveyCompletedCardVm) {
      surveyCompletedCardVm.$destroy();
      surveyCompletedCardVm = null;
    }
    // Counts are plain HTML and are now current. Keep the hook element itself so its
    // data-refresh-url survives for the next refresh; Vue mounts on the card child.
    $hook.html(html);
    try {
      const cardEl = $hook.children().get(0);
      if (cardEl) surveyCompletedCardVm = new Vue({ el: cardEl });
    } catch (e) {
      // Counts already updated; only the bar-chart needs Vue.
    }
    // Re-bind the card's side-panel drill-down links (bound once at page load).
    $hook
      .find('[data-sidepanel]')
      .not((_index, el) => el.dataset.sidepanel === 'false')
      .on('click', SidePanel.loadAndShow.bind(SidePanel));
  });
}

const QuestionsResponsesChanges = {
  start() {
    SidePanel.onUpdate(($sidepanel) => {
      const $forms = $sidepanel.find('.js-questions-responses');
      if (!$forms.length) return;

      $forms.on('ajax:success', this._onSaveSuccess);

      // Cancel button reuses the .toggle-editing handler, which fires
      // a `toggle:editing` event on the .form--inplace card with the new
      // editing state (true=entered, false=exited). When the user exits
      // editing without saving, revert input values to their dirrty
      // initial value and mark the form clean so Close won't warn.
      $sidepanel.find('.form--inplace').on('toggle:editing.qrdirty', this._onToggleEditing);

      // Intercept Close. Registered first because v2.js loads modules
      // alphabetically and questions_responses_changes < sidepanel, so this
      // onUpdate handler runs before SidePanel's, attaching its click
      // listener before SidePanel.reset's listener.
      $sidepanel.find('[data-sidepanel-close]').on('click.qrdirty', (e) => {
        if (this._anyDirty($sidepanel)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this._confirmClose($sidepanel);
        }
      });
    });
  },

  _onSaveSuccess(e) {
    // The saved values are now the clean baseline. Two separate dirty trackers watch
    // this form, so both must be cleared after a successful save:
    //   1. dirrty('setClean') resets the plugin's internal state (the Close-button
    //      check in this module reads dirrty('isDirty')).
    //   2. dirrty('setClean') does NOT emit a 'clean' event, so SidePanelForm's
    //      module-level _dirtyForm flag (checked when navigating away, e.g. opening a
    //      graph drill-down) stays set. Fire 'clean' so SidePanelForm clears it.
    const $form = $(e.currentTarget);
    $form.dirrty('setClean');
    $form.trigger('clean');
    $('.js-track-responses-table').trigger('datatable:reload');
    refreshSurveyQuestionGraphs();
    refreshSurveyCompletedCard();
  },

  _onToggleEditing(e, isEditing) {
    if (isEditing) return;
    const $form = $(e.currentTarget).find('form.js-questions-responses');
    if (!$form.length) return;
    $form.find('input, textarea, select').each(function () {
      const initial = $(this).attr('data-dirrty-initial-value');
      if (initial !== undefined) $(this).val(initial);
    });
    $form.dirrty('setClean');
  },

  _anyDirty($sidepanel) {
    let dirty = false;
    $sidepanel.find('form.js-questions-responses').each(function () {
      if ($(this).dirrty('isDirty')) {
        dirty = true;
        return false;
      }
    });
    return dirty;
  },

  _confirmClose($sidepanel) {
    const scope = 'js.my_events.questions_responses.unsaved_close';
    sweetAlert({
      title: I18n.t(`${scope}.title`),
      text: I18n.t(`${scope}.text`),
      icon: 'warning',
      buttons: [I18n.t('shared.buttons.cancel'), I18n.t(`${scope}.confirm`)]
    }).then((confirmed) => {
      if (!confirmed) return;
      $sidepanel.find('form.js-questions-responses').each(function () {
        $(this).dirrty('setClean');
      });
      SidePanel.reset();
    });
  }
};

export default QuestionsResponsesChanges;
